import { CableRoute } from "@domain/project/floorplan/wiring/cable-route";
import { FloorplanItemTextKonva } from "@project/floorplanner/konva/floorplan-item-text-konva";
import { FloorplanWorkspaceKonvaAdapter } from "@project/floorplanner/konva/floorplan-workspace-konva.adapter";
import { LabelLayoutKonva } from "@project/floorplanner/konva/label-layout-konva";
import Konva from "konva";
import { Group } from "konva/lib/Group";
import { Line } from "konva/lib/shapes/Line";
import { Text } from "konva/lib/shapes/Text";
import Circle = Konva.Circle;
import Rect = Konva.Rect;

export class CableRouteKonva {
  static readonly COLOR_CODE = "#0077cc";

  static init(cableRoute: CableRoute): Konva.Group {
    const parentGroup = new Konva.Group();

    const routeGroup = new Konva.Group({
      x: cableRoute.x,
      y: cableRoute.y,
      rotation: cableRoute.rotation,
      height: cableRoute.height,
      width: cableRoute.width,
      draggable: false,
    });
    routeGroup.setAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY, cableRoute);
    routeGroup.setAttr("minWidth", 70);
    routeGroup.setAttr("minHeight", 20);

    const rect = new Konva.Rect({
      width: cableRoute.width,
      height: cableRoute.height,
    });

    const line = new Line({
      points: [0, 0, cableRoute.width, 0],
      stroke: this.COLOR_CODE,
      strokeWidth: 4,
    });

    const circleLeft = new Konva.Circle({
      radius: 0,
      fill: "white",
      stroke: this.COLOR_CODE,
      strokeWidth: 4,
    });

    const circleRight = new Konva.Circle({
      radius: 0,
      fill: "white",
      stroke: this.COLOR_CODE,
      strokeWidth: 4,
      x: cableRoute.width,
    });

    const textGroup = FloorplanItemTextKonva.init(cableRoute);
    const text = FloorplanItemTextKonva.getText(textGroup);
    this.scaleText(text, textGroup, routeGroup);

    routeGroup.add(rect);
    routeGroup.add(line);
    routeGroup.add(circleLeft);
    routeGroup.add(circleRight);

    parentGroup.add(routeGroup, textGroup);

    this.setShapeDimensions(routeGroup, line, circleLeft, circleRight, rect);
    this.setTextPosition(textGroup, routeGroup);

    routeGroup.on("transformstart", () => {
      textGroup.hide();
    });

    routeGroup.on("transform", () => {
      routeGroup.width(routeGroup.width() * routeGroup.scaleX());
      routeGroup.height(routeGroup.height() * routeGroup.scaleY());

      rect.height(routeGroup.height());
      rect.width(routeGroup.width());

      this.setShapeDimensions(routeGroup, line, circleLeft, circleRight, rect);
      routeGroup.scaleX(1);
      routeGroup.scaleY(1); // Prevents actual scaling and keeps proportions manually
    });

    routeGroup.on("transformend", () => {
      cableRoute.updateForm(routeGroup.width(), routeGroup.height(), routeGroup.rotation(), routeGroup.x(), routeGroup.y());
      this.scaleText(text, textGroup, routeGroup);
      this.setTextPosition(textGroup, routeGroup);
      const labelHidden = textGroup.getAttr("labelHidden") === true;
      if (!labelHidden) {
        textGroup.show();
      }
    });

    routeGroup.on("dragmove", () => {
      this.setTextPosition(textGroup, routeGroup);
    });

    return parentGroup;
  }

  static refreshLength(cableRoute: CableRoute, parentGroup: Konva.Group) {
    const cableRouteGroup = parentGroup.getChildren()[0] as Konva.Group;
    const rect = cableRouteGroup.getChildren()[0] as Rect;
    const line = cableRouteGroup.getChildren()[1] as Line;
    const circleRight = cableRouteGroup.getChildren()[3] as Circle;
    cableRouteGroup.width(cableRoute.width + circleRight.radius());
    rect.width(cableRoute.width);
    line.points([0, 0, cableRoute.width, 0]);
    circleRight.x(cableRoute.width);
    this.refreshItemText(cableRoute, parentGroup);
  }

  static refreshItemText(cableRoute: CableRoute, group: Konva.Group) {
    const positionId = group.getChildren()[1] as Group;
    const routeGroup = group.getChildren()[0] as Konva.Group;

    FloorplanItemTextKonva.refreshText(cableRoute, positionId);
    this.setTextPosition(positionId, routeGroup);
  }

  private static setShapeDimensions(group: Konva.Group, line: Line, circleLeft: Circle, circleRight: Circle, rect: Rect): void {
    const strokeWidth = rect.height() / 4;
    const radius = rect.height() / 2;

    rect.y(0);

    line.points([0, 0, rect.width(), 0]);
    line.strokeWidth(strokeWidth);
    line.y(rect.height() / 2);

    circleLeft.radius(radius);
    circleLeft.strokeWidth(strokeWidth);
    circleLeft.x(circleLeft.radius());
    circleLeft.y(rect.height() / 2);

    circleRight.radius(radius);
    circleRight.strokeWidth(strokeWidth);
    circleRight.x(rect.width() - circleRight.radius());
    circleRight.y(rect.height() / 2);
  }

  private static setTextPosition(itemText: Group, group: Konva.Group): void {
    const item = group.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY);
    LabelLayoutKonva.placeLabelForItem(itemText, group, item);
  }

  private static scaleText(itemText: Text, textGroup: Group, cableRouteGroup: Group) {
    itemText.fontSize(cableRouteGroup.height() / 2);
    FloorplanItemTextKonva.updateBackground(textGroup);
  }
}
